import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { CreditCard, History, Loader2, Users, Wallet } from "lucide-react";
import { formatUsdFromCents } from "@crewledger/shared/billing";
import type { BillingPlan, BillingSummary, BillingUsageRow, BillingPlanTier } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader } from "@/components/design";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  createBillingSnapshot,
  fetchBillingPlans,
  fetchBillingSummary,
  fetchBillingUsage,
  updateBillingPlan,
} from "./api";
import { useBillingPermissions } from "./use-billing-permissions";

function statusBadgeClass(status: string): string {
  switch (status) {
    case "ACTIVE":
      return "bg-green-50 text-green-700 border-green-200";
    case "TRIALING":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "PAST_DUE":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function formatMoney(cents: number | null): string {
  return formatUsdFromCents(cents);
}

export function BillingPage() {
  const { canRead, canWrite, isReadOnly } = useBillingPermissions();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [usage, setUsage] = useState<BillingUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingPlan, setSavingPlan] = useState(false);
  const [snapshottingId, setSnapshottingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, plansRes, usageRes] = await Promise.all([
        fetchBillingSummary(),
        fetchBillingPlans(),
        fetchBillingUsage({ page: 1, pageSize: 24 }),
      ]);
      setSummary(summaryRes.data);
      setPlans(plansRes.data);
      setUsage(usageRes.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canRead) return;
    void load();
  }, [canRead, load]);

  if (!canRead) return <Navigate to="/dashboard" replace />;

  const handlePlanChange = async (planTier: BillingPlanTier) => {
    if (!canWrite || summary?.subscription.planTier === planTier) return;
    setSavingPlan(true);
    try {
      await updateBillingPlan(planTier);
      await load();
    } finally {
      setSavingPlan(false);
    }
  };

  const handleSnapshot = async (payPeriodId: string) => {
    if (!canWrite) return;
    setSnapshottingId(payPeriodId);
    try {
      await createBillingSnapshot(payPeriodId);
      await load();
    } finally {
      setSnapshottingId(null);
    }
  };

  return (
    <div className="space-y-8 pb-10">
      <PageHeader
        title="Billing"
        description="Usage-based pricing per active worker each pay period. Stripe integration coming soon."
        badge={
          summary ? (
            <Badge variant="outline" className={cn("capitalize", statusBadgeClass(summary.subscription.status))}>
              {summary.subscription.status.toLowerCase().replaceAll("_", " ")}
            </Badge>
          ) : null
        }
      />

      {loading && !summary ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading billing…
        </div>
      ) : summary ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <CreditCard className="h-4 w-4" />
                  Current plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-navy-deep">{summary.plan.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{summary.plan.description}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Users className="h-4 w-4" />
                  Active workers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-navy-deep">{summary.currentPeriodUsage.activeWorkers}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {summary.currentPayPeriod
                    ? `In ${summary.currentPayPeriod.label}`
                    : "No open pay period"}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Wallet className="h-4 w-4" />
                  Estimated bill
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-navy-deep">
                  {summary.currentPeriodUsage.isCustomPricing
                    ? "Custom"
                    : formatMoney(summary.currentPeriodUsage.estimatedAmountCents)}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {summary.currentPeriodUsage.isCustomPricing
                    ? "Enterprise pricing"
                    : summary.currentPeriodUsage.unitPriceCents != null
                      ? `${formatMoney(summary.currentPeriodUsage.unitPriceCents)} × ${summary.currentPeriodUsage.activeWorkers} workers`
                      : "Current pay period"}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pay period</CardTitle>
              </CardHeader>
              <CardContent>
                {summary.currentPayPeriod ? (
                  <>
                    <p className="text-lg font-semibold text-navy-deep">{summary.currentPayPeriod.label}</p>
                    <p className="mt-1 text-sm capitalize text-muted-foreground">
                      {summary.currentPayPeriod.status.toLowerCase().replaceAll("_", " ")}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No pay period selected</p>
                )}
              </CardContent>
            </Card>
          </div>

          <section className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-navy-deep">Plans</h2>
                <p className="text-sm text-muted-foreground">
                  {isReadOnly ? "View-only access" : "Change plan without Stripe (internal billing only)"}
                </p>
              </div>
              {savingPlan ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {plans.map((plan) => {
                const selected = summary.subscription.planTier === plan.tier;
                return (
                  <Card
                    key={plan.tier}
                    className={cn(selected && "border-navy-blueprint ring-1 ring-navy-blueprint/30")}
                  >
                    <CardHeader>
                      <CardTitle className="text-base">{plan.label}</CardTitle>
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-navy-deep">
                        {plan.pricePerWorkerCents != null
                          ? `${formatMoney(plan.pricePerWorkerCents)}/worker`
                          : "Custom"}
                      </p>
                      {canWrite ? (
                        <Button
                          className="mt-4 w-full"
                          variant={selected ? "secondary" : "default"}
                          disabled={selected || savingPlan}
                          onClick={() => void handlePlanChange(plan.tier)}
                        >
                          {selected ? "Current plan" : "Select plan"}
                        </Button>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-navy-deep">Usage by pay period</h2>
              <p className="text-sm text-muted-foreground">
                Active worker = any worker with at least one time entry in the period
              </p>
            </div>

            {usage.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No pay periods yet"
                description="Usage will appear once pay periods and time entries exist."
              />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border/60 bg-card-white">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b bg-bg-light/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Pay period</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Active workers</th>
                      <th className="px-4 py-3 font-medium">Rate</th>
                      <th className="px-4 py-3 font-medium">Estimated</th>
                      <th className="px-4 py-3 font-medium">Recorded</th>
                      {canWrite ? <th className="px-4 py-3 font-medium" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {usage.map((row) => (
                      <tr key={row.payPeriodId} className="border-b last:border-0 hover:bg-bg-light/40">
                        <td className="px-4 py-3">
                          <p className="font-medium text-navy-deep">{row.payPeriodLabel}</p>
                          <p className="text-xs text-muted-foreground">
                            {row.startDate} → {row.endDate}
                          </p>
                        </td>
                        <td className="px-4 py-3 capitalize">
                          {row.payPeriodStatus.toLowerCase().replaceAll("_", " ")}
                        </td>
                        <td className="px-4 py-3 font-mono tabular-nums">{row.activeWorkers}</td>
                        <td className="px-4 py-3 font-mono tabular-nums">
                          {row.unitPriceCents != null ? formatMoney(row.unitPriceCents) : "Custom"}
                        </td>
                        <td className="px-4 py-3 font-mono tabular-nums">
                          {row.estimatedAmountCents != null ? formatMoney(row.estimatedAmountCents) : "Custom"}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {row.isSnapshot && row.snapshotAt
                            ? new Date(row.snapshotAt).toLocaleString()
                            : "Live estimate"}
                        </td>
                        {canWrite ? (
                          <td className="px-4 py-3">
                            {!row.isSnapshot ? (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={snapshottingId === row.payPeriodId}
                                onClick={() => void handleSnapshot(row.payPeriodId)}
                              >
                                {snapshottingId === row.payPeriodId ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  "Save snapshot"
                                )}
                              </Button>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-dashed border-border/80 bg-bg-light/40 p-6">
            <div className="flex items-start gap-3">
              <History className="mt-0.5 h-5 w-5 text-muted-foreground" />
              <div>
                <h2 className="text-lg font-semibold text-navy-deep">Billing history</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Invoice and payment history will appear here once Stripe is connected. Usage snapshots
                  above are stored internally for reporting.
                </p>
              </div>
            </div>
          </section>
        </>
      ) : (
        <EmptyState
          icon={CreditCard}
          title="Billing unavailable"
          description="Unable to load billing data. Try again later."
        />
      )}
    </div>
  );
}
